import { Icon, Typography } from '@smartgift/frontend-toolkit';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { configs, constants } from '@hero/configs';
import { Table, OrderStatusBadge, UserName } from '@hero/molecules';
import { useGetContacts } from '@hero/services/contact-service/hooks';
import { getSafeInnerHtml } from '@hero/utils';

import * as S from './style';

function CustomizeMessageTable({
  defaultMessage,
  customMessages = {},
  setCustomMessages,
  selectedContacts,
  editable
}) {
  const { t } = useTranslation();
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50
  });

  const [globalFilters, setGlobalFilters] = useState({});
  const { data: contacts, isLoading } = useGetContacts(
    {
      filters: {
        searchTerm: globalFilters.searchTerm,
        ids: selectedContacts
      },
      pagination: {
        page: pageIndex + 1,
        limit: pageSize
      }
    },
    {
      select(responseData) {
        return {
          ...responseData,
          docs: responseData.docs.map(doc => {
            return {
              ...doc,
              giftMessage: customMessages?.[doc.id]?.giftMessage || defaultMessage
            };
          })
        };
      }
    }
  );

  function firstNameCell({
    row: {
      original: { firstName, lastName }
    }
  }) {
    return <UserName hasNameAvatar firstName={firstName} lastName={lastName} />;
  }

  const columns = React.useMemo(
    () => [
      {
        header: t('name'),
        size: 220,
        accessorKey: 'firstName',
        cell: firstNameCell
      },
      {
        id: 'giftMessage',
        header: t('giftMessage'),
        editable: editable,
        size: 420,
        accessorKey: 'giftMessage',
        type: 'textEditor',
        defaultProps: {
          limit: constants?.general?.giftMessageLimit
        },
        ...(!editable
          ? { cell: ({ getValue }) => <Typography {...getSafeInnerHtml(getValue())} /> }
          : {})
      },
      {
        id: 'revert',
        header: '',
        cell: ({ row, table }) => {
          const customMessages = table.getState().customMessages;

          if (!editable) {
            return (
              <OrderStatusBadge
                status={customMessages[row.original.id] ? 'Customized' : 'Original'}
              />
            );
          }
          if (!editable || !customMessages[row.original.id]?.message) return null;

          return (
            <S.RevertButton
              size="sm"
              variant="outlined"
              startIcon={<Icon name="revert" />}
              onClick={() => {
                setCustomMessages({
                  ...customMessages,
                  [row.original.id]: {
                    giftMessage: ''
                  }
                });
              }}
            >
              {t('revert')}
            </S.RevertButton>
          );
        }
      }
    ],
    [editable]
  );

  const updateData = (originalRow, columnId, value) => {
    if (value?.trim() !== defaultMessage?.trim()) {
      if (configs?.experience?.customGiftMessageEditorEnabled) {
        const wordArray = value.split('\n');
        wordArray.length = 3;

        setCustomMessages({
          ...customMessages,
          [originalRow.id]: {
            giftMessage: value
          }
        });
      } else {
        setCustomMessages({
          ...customMessages,
          [originalRow.id]: {
            giftMessage: value
          }
        });
      }
    }
  };

  return (
    <Table
      columns={columns}
      data={contacts?.docs || []}
      globalFilters={globalFilters}
      state={{
        customMessages
      }}
      pagination={{
        pageIndex,
        pageSize,
        total: contacts?.pagination?.total
      }}
      rowHeight={!editable && 52}
      updateData={updateData}
      manualPagination
      loading={isLoading}
      setGlobalFilters={setGlobalFilters}
      setPagination={setPagination}
      pageCount={contacts?.pagination?.pages}
      enabled={{ filter: false, header: true }}
    />
  );
}

export { CustomizeMessageTable };
